import { Box, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'
import { Outlet, useLocation } from 'react-router-dom'
import { findActiveAdminItem } from '@/features/dashboard/admin/adminNavModel'
import useAuth from '@/hooks/useAuth'
import { hasRole } from '@/utils/hasRole'
import AdminNavMenu from './AdminNavMenu'
import AdminSideNav from './AdminSideNav'
import { ADMIN_AREAS } from './adminAreas'
import useAdminNav from './useAdminNav'

const SIDE_NAV_WIDTH = 248

/**
 * The shell every page of an admin area (/admin/*, /site-admin/*) renders inside: a grouped nav down the left on
 * desktop (the pattern Linear, cal.com and Google Admin all use for settings),
 * and a "jump to" menu above the page on smaller screens. The main app sidebar
 * carries a single link per area; this is how you move between its pages.
 *
 * Non-admins get the bare page — its own RoleGuard explains they can't see it —
 * rather than a nav full of links they can't use.
 */
export default function AdminLayout({ area = 'admin' }) {
    const config = ADMIN_AREAS[area]
    const { user } = useAuth()
    const { pathname } = useLocation()
    const groups = useAdminNav(area)
    const mdUp = useMediaQuery((theme) => theme.breakpoints.up('md'))

    if (!hasRole(user, config.roles)) {
        return <Outlet />
    }

    const activeItem = findActiveAdminItem(groups, pathname)
    const onOverview =
        pathname === config.rootPath || pathname === `${config.rootPath}/`

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            {mdUp ? (
                <Box
                    sx={{
                        width: SIDE_NAV_WIDTH,
                        flexShrink: 0,
                        position: 'sticky',
                        top: 80,
                        maxHeight: 'calc(100vh - 96px)',
                        overflowY: 'auto',
                        pt: 3,
                        pl: 2,
                        pb: 3
                    }}
                >
                    <AdminSideNav
                        area={config}
                        groups={groups}
                        activeId={activeItem?.id ?? null}
                        onOverview={onOverview}
                    />
                </Box>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {mdUp ? null : (
                    <AdminNavMenu
                        area={config}
                        groups={groups}
                        activeItem={activeItem}
                        onOverview={onOverview}
                    />
                )}
                <Outlet />
            </Box>
        </Box>
    )
}

AdminLayout.propTypes = {
    area: PropTypes.oneOf(Object.keys(ADMIN_AREAS))
}

/** The Site admin area's shell, registered by name in routeUtils' layoutMap. */
export function SiteAdminLayout() {
    return <AdminLayout area="siteAdmin" />
}
